<script setup>
/**
 * Security.
 *
 * The SAML card keeps its three states - the plan does not include SAML
 * (paywall), the instance allows SAML logins (the choice cards and the
 * identity-provider form), or the instance does not allow them at all, in
 * which case the page still exists and says so.
 *
 * Below it, a pointer card: sessions and multi-factor authentication are
 * per-person settings that live on the personal Profile page, and this is
 * where people come looking for them.
 */
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { INSTALLATION_TYPES } from 'dashboard/constants/installationTypes';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import SettingsPageShell from 'dashboard/components-next/Settings/SettingsPageShell.vue';
import SettingsSection from 'dashboard/components-next/Settings/SettingsSection.vue';
import SamlSettings from './components/SamlSettings.vue';
import SamlPaywall from './components/SamlPaywall.vue';

const route = useRoute();
const { shouldShow, shouldShowPaywall } = usePolicy();

const personalSecurityRoute = computed(() => ({
  name: 'profile_settings_index',
  params: { accountId: route.params.accountId },
}));

const allowedLoginMethods = computed(
  () => window.tolaConfig.allowedLoginMethods || ['email']
);

const isSamlSsoEnabled = computed(() =>
  allowedLoginMethods.value.includes('saml')
);

const shouldShowSaml = computed(() => {
  const hasPermission = shouldShow(
    FEATURE_FLAGS.SAML,
    ['administrator'],
    [INSTALLATION_TYPES.CLOUD, INSTALLATION_TYPES.ENTERPRISE]
  );
  return hasPermission && isSamlSsoEnabled.value;
});

const showPaywall = computed(() => shouldShowPaywall('saml'));
</script>

<template>
  <SettingsPageShell
    :title="$t('SIDEBAR.LOGIN_SETTINGS')"
    :description="$t('SECURITY_SETTINGS.DESCRIPTION')"
  >
    <SamlPaywall v-if="showPaywall" />
    <SamlSettings v-else-if="shouldShowSaml" />
    <SettingsSection v-else>
      <p class="px-4 py-3.5 text-sm text-n-slate-11">
        {{ $t('SECURITY_SETTINGS.SAML_DISABLED_MESSAGE') }}
      </p>
    </SettingsSection>

    <SettingsSection
      :title="$t('SECURITY_SETTINGS.PERSONAL_SECURITY.TITLE')"
      :description="$t('SECURITY_SETTINGS.PERSONAL_SECURITY.NOTE')"
    >
      <div class="flex items-center justify-between gap-4 px-4 py-3.5">
        <p class="mb-0 text-sm text-n-slate-11">
          {{ $t('SECURITY_SETTINGS.PERSONAL_SECURITY.DESCRIPTION') }}
        </p>
        <RouterLink
          :to="personalSecurityRoute"
          class="flex-shrink-0 text-sm text-n-blue-11 hover:underline"
        >
          {{ $t('SECURITY_SETTINGS.PERSONAL_SECURITY.LINK') }}
        </RouterLink>
      </div>
    </SettingsSection>
  </SettingsPageShell>
</template>
